import { Panel } from '@/components/admin/Field'
import { lumi } from '@/lib/lumi'
import type { HostMessage } from '@/types/host'
import { CheckCheck, Inbox, MapPin, Phone, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

function formatDate(value?: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

interface HostMessagesProps {
  hostId?: string
  showHost?: boolean
}

function HostMessages({ hostId, showHost = false }: HostMessagesProps) {
  const [items, setItems] = useState<HostMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'new' | 'seen'>('new')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.host_messages.list({
        filter: hostId ? { hostId } : {},
        sort: { createdAt: -1 },
        limit: 200,
      })
      const all = (list || []) as HostMessage[]
      // O locutor só vê o que o administrador aprovou (recados antigos sem status contam como aprovados).
      setItems(showHost ? all : all.filter((item) => !item.status || item.status === 'approved'))
    } catch (error) {
      console.error('Falha ao carregar recadinhos:', error)
      toast.error('Não foi possível carregar os recadinhos.')
    } finally {
      setLoading(false)
    }
  }, [hostId, showHost])

  useEffect(() => {
    void load()
  }, [load])

  const setRead = async (id: string | undefined, read: boolean) => {
    if (!id) return
    try {
      await lumi.entities.host_messages.update(id, { read })
      setItems((prev) => prev.map((item) => (item._id === id ? { ...item, read } : item)))
    } catch (error) {
      console.error('Falha ao atualizar recadinho:', error)
      toast.error('Não foi possível atualizar o recadinho.')
    }
  }

  const remove = async (id?: string) => {
    if (!id) return
    try {
      await lumi.entities.host_messages.delete(id)
      setItems((prev) => prev.filter((item) => item._id !== id))
      toast.success('Recadinho apagado.')
    } catch (error) {
      console.error('Falha ao apagar recadinho:', error)
      toast.error('Não foi possível apagar o recadinho.')
    }
  }

  const { novos, vistos } = useMemo(() => {
    return {
      novos: items.filter((item) => !item.read),
      vistos: items.filter((item) => item.read),
    }
  }, [items])

  const current = view === 'new' ? novos : vistos

  return (
    <Panel title={showHost ? 'Recadinhos de todos os locutores' : 'Recadinhos'}>
      <div className="mb-4 flex gap-2">
        <button
          type="button"
          onClick={() => setView('new')}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${
            view === 'new' ? 'bg-[#0b2ea8] text-white' : 'bg-slate-100 text-slate-600'
          }`}
        >
          <Inbox size={16} /> Novos {novos.length ? `(${novos.length})` : ''}
        </button>
        <button
          type="button"
          onClick={() => setView('seen')}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${
            view === 'seen' ? 'bg-[#0b2ea8] text-white' : 'bg-slate-100 text-slate-600'
          }`}
        >
          <CheckCheck size={16} /> Vistos {vistos.length ? `(${vistos.length})` : ''}
        </button>
      </div>

      {loading ? <div className="h-20 animate-pulse rounded-xl bg-slate-100" /> : null}
      <div className="space-y-3">
        {current.map((item) => (
          <article
            key={item._id}
            className={`rounded-xl border p-4 ${item.read ? 'border-slate-200 bg-white' : 'border-[#ffd21f] bg-yellow-50'}`}
          >
            {showHost && item.hostName ? (
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-[#0b2ea8]">Para {item.hostName}</p>
            ) : null}
            <p className="whitespace-pre-line text-sm leading-relaxed">{item.message}</p>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
              <span className="font-bold text-slate-800">{item.senderName}</span>
              <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1 text-[#0b2ea8]">
                <Phone size={12} /> {item.phone}
              </a>
              <span className="inline-flex items-center gap-1">
                <MapPin size={12} /> {item.city}
              </span>
              <span>{formatDate(item.createdAt)}</span>
            </div>
            <div className="mt-3 flex gap-3 text-xs font-bold">
              {item.read ? (
                <button type="button" onClick={() => void setRead(item._id, false)} className="inline-flex items-center gap-1 text-slate-500">
                  <Inbox size={14} /> Mover para novos
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void setRead(item._id, true)}
                  className="inline-flex items-center gap-1 text-[#0b2ea8]"
                >
                  <CheckCheck size={14} /> Marcar como visto
                </button>
              )}
              <button type="button" onClick={() => void remove(item._id)} className="inline-flex items-center gap-1 text-red-500">
                <Trash2 size={14} /> Apagar
              </button>
            </div>
          </article>
        ))}
        {!loading && !current.length ? (
          <p className="text-sm text-slate-500">
            {view === 'new'
              ? 'Nenhum recadinho novo por enquanto.'
              : 'Ainda não marcou nenhum recadinho como visto.'}
          </p>
        ) : null}
      </div>
    </Panel>
  )
}

export default HostMessages
